import { PageTitleRow } from "../../components/layout/PageTitleRow";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, LayoutDashboard, RefreshCw, Save } from "lucide-react";
import { AppLayout, PageBackLink } from "../../components/layout";
import { ContentCard, EmptyState, LoadingState, ui } from "../../components/ui";
import { cn } from "../../lib/utils";
import { fetchDashboardConfigs, updateDashboardConfigs } from "../../services/dashboardConfigService";

/** Dashboards grouped by service, in the order the API returns them. */
function groupByService(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const service = String(row.serviceName ?? "").trim();
    groups.set(service, [...(groups.get(service) ?? []), row]);
  });
  return [...groups].map(([service, items]) => ({ service, items }));
}

export function DashboardConfigPage() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  /** Ticked values not yet saved, by dashboardId. */
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    fetchDashboardConfigs()
      .then(setRows)
      .catch((error) => {
        setRows([]);
        setLoadError(error.message || t("dashboardConfig.errorLoad"));
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => load(), [load]);

  const groups = useMemo(() => groupByService(rows), [rows]);

  const valueOf = (row) => edits[row.dashboardId] ?? row.enabled;

  /** The dashboards whose box differs from what is stored, as the update takes them. */
  const changed = rows
    .filter((row) => valueOf(row) !== row.enabled)
    .map((row) => ({ dashboardId: row.dashboardId, enabled: valueOf(row) }));

  const toggle = (row, enabled) => {
    setEdits((current) => ({ ...current, [row.dashboardId]: enabled }));
    setSaveError("");
    setSaved(false);
  };

  /** Every changed dashboard in one request. */
  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await updateDashboardConfigs(changed);
      setRows((current) => current.map((row) => ({ ...row, enabled: valueOf(row) })));
      setEdits({});
      setSaved(true);
    } catch (error) {
      setSaveError(error.message || t("dashboardConfig.errorSave"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout formLayout="wide" className="space-y-5">
      <PageTitleRow back={<PageBackLink to="/" />}>
        <h1 className={ui.pageTitle}>{t("dashboardConfig.title")}</h1>
      </PageTitleRow>

      {loadError ? (
        <div className="flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 self-start rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 transition hover:bg-rose-100 sm:self-auto"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("dashboardConfig.retry")}
          </button>
        </div>
      ) : null}

      {loading ? (
        <ContentCard>
          <LoadingState message={t("dashboardConfig.loading")} />
        </ContentCard>
      ) : groups.length === 0 ? (
        <ContentCard>
          <EmptyState icon={LayoutDashboard} title={t("dashboardConfig.emptyTitle")} message={t("dashboardConfig.emptyMessage")} />
        </ContentCard>
      ) : (
        <>
          {/* One card; each service is a section whose checkbox box grows with its dashboards — no scrolling. */}
          <ContentCard bodyClassName="form-section">
            <div className="space-y-5">
              {groups.map((group, index) => (
                <section
                  key={group.service || "uncategorised"}
                  className={cn(index > 0 && "border-t border-(--color-text-border) pt-5")}
                >
                  <h3 className="mb-4 flex flex-wrap items-center gap-2 text-sm font-bold text-foreground">
                    {group.service || t("dashboardConfig.uncategorised")}
                    <span className="rounded-full bg-workspace px-2 py-0.5 text-[11px] font-bold per-file-hint">
                      {t("dashboardConfig.enabledCount", {
                        enabled: group.items.filter(valueOf).length,
                        total: group.items.length,
                      })}
                    </span>
                  </h3>
                  <div className="grid w-full grid-cols-1 gap-1.5 rounded-xl border border-(--color-text-border) bg-white p-2 sm:grid-cols-2 lg:grid-cols-3">
                    {group.items.map((row) => (
                      <label
                        key={row.dashboardId}
                        title={row.dashboardName}
                        className="flex h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-foreground transition hover:bg-workspace/60"
                      >
                        <input
                          type="checkbox"
                          checked={valueOf(row)}
                          disabled={saving}
                          onChange={(event) => toggle(row, event.target.checked)}
                          className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
                        />
                        <span className="min-w-0 truncate select-none font-medium">{row.dashboardName}</span>
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </ContentCard>

          <div className="form-actions flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            {saveError ? (
              <p className="text-[12px] font-semibold text-rose-600 sm:mr-auto">{saveError}</p>
            ) : saved ? (
              <p className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700 sm:mr-auto">
                <Check className="h-3.5 w-3.5" />
                {t("dashboardConfig.saved")}
              </p>
            ) : null}
            <button type="button" onClick={save} disabled={saving || !changed.length} className={cn(ui.primaryBtn, "px-6")}>
              <Save className="h-4 w-4" />
              {saving ? t("dashboardConfig.saving") : t("dashboardConfig.save")}
            </button>
          </div>
        </>
      )}
    </AppLayout>
  );
}

export default DashboardConfigPage;
